/**
 * PublicCustomLayerRenderer
 *
 * Public-viewer counterpart of the authenticated {@link CustomLayerRenderer}.
 * Renders the share's exposed custom layers that are currently toggled on,
 * fetching each layer's GeoJSON through the token-gated public endpoint (no
 * session). Reuses the shared {@link CustomLayerSource} + {@link useCustomLayerGeoJson}
 * — only the fetcher differs from the authenticated renderer. Visibility is
 * driven by the same `mapLayers.customLayerVisibility` map the public layers
 * panel toggles.
 */
import { memo, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  CustomLayerSource,
  useCustomLayerGeoJson,
} from '@/components/map/CustomLayerRenderer';
import { publicShareService } from '@/services/api/publicShareService';
import { useGeoMarkupStore } from '@/store';
import { selectCustomLayerVisibility } from '@/store/selectors';
import type { PublicCustomLayer } from '@/types/share';

/** Renders a single shared custom layer via the token-gated public GeoJSON endpoint. */
const SinglePublicLayerRenderer = memo(function SinglePublicLayerRenderer({
  token,
  layer,
}: {
  token: string;
  layer: PublicCustomLayer;
}) {
  const fetcher = useCallback(
    async (signal: AbortSignal) => {
      const response = await publicShareService.getCustomLayerGeoJson(
        token,
        layer.id,
        signal
      );
      return response.data;
    },
    [token, layer.id]
  );

  const geoJson = useCustomLayerGeoJson(`public:${token}:${layer.id}`, fetcher);

  if (!geoJson) return null;

  return (
    <CustomLayerSource
      layerId={layer.id}
      geoJson={geoJson}
      styleJson={layer.styleJson}
    />
  );
});

interface PublicCustomLayerRendererProps {
  token: string;
  layers: PublicCustomLayer[];
}

export const PublicCustomLayerRenderer = memo(
  function PublicCustomLayerRenderer({
    token,
    layers,
  }: PublicCustomLayerRendererProps) {
    const visibility = useGeoMarkupStore(
      useShallow(selectCustomLayerVisibility)
    );

    const visibleLayers = useMemo(
      () => layers.filter((layer) => visibility[layer.id] ?? true),
      [layers, visibility]
    );

    if (visibleLayers.length === 0) return null;

    return (
      <>
        {visibleLayers.map((layer) => (
          <SinglePublicLayerRenderer
            key={layer.id}
            token={token}
            layer={layer}
          />
        ))}
      </>
    );
  }
);
